import React, { useContext, useEffect, useState } from "react";
import {
  GridContainer,
  BillingSettingsFormRootArea,
  GridItem,
  ImagePreviewContainer,
  ImagePreview,
} from "./BillingSettingsForm.styles";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Input,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import AuthContext from "../../../context/AuthContext";
import { useEffectOnce } from "../../../utils/effects";
import {
  getBillingSettingsById,
  createBillingSettings,
  updateBillingSettings,
} from "./BillingSettingsForm.thunk";
import {
  billingSettingsInitialValues,
  billingSettingsInitialValidationSchema,
  setBillingSettingsValues,
  toContactFormDataValue,
  BANNER_LOGO_MESSAGES,
  getImageDimensionsFromFile,
  isValidBannerDimensions,
} from "./BillingSettingsForm.resources";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import TextField from "../../textField/TextField";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

const BillingSettingsForm = (props) => {
  const dispatch = useDispatch();
  const { token, acquirerData } = useContext(AuthContext);
  const [previewLogoUrl, setPreviewLogoUrl] = useState(null);
  // Kept while the Acquirer Logo JSX block is commented out; restore UI and remove this disable when re-enabling.
  // eslint-disable-next-line no-unused-vars -- preview is referenced from commented JSX below
  const [previewAcquirerLogoUrl, setPreviewAcquirerLogoUrl] = useState(null);

  const merchantId = acquirerData?.merchantID;

  const {
    billingSettingsData,
    billingSettingsId,
    statusRetrieve,
    statusCreate,
    statusUpdate,
  } = useSelector((state) => state.billingSettingsForm);

  const modeForm =
    billingSettingsData && billingSettingsId ? "update" : "create";
  const isLoading =
    statusRetrieve === "loading" ||
    statusCreate === "loading" ||
    statusUpdate === "loading";

  useEffectOnce(() => {
    if (merchantId) {
      dispatch(getBillingSettingsById({ merchantId, token }));
    }
  });

  // Effect to set form values when billingSettingsData changes (for editing)
  useEffect(() => {
    if (billingSettingsData && merchantId) {
      setBillingSettingsValues({ billingSettingsData, setFieldValue });
      if (billingSettingsData.logo_url) {
        setPreviewLogoUrl(billingSettingsData.logo_url);
        setFieldValue("logo", billingSettingsData.logo_url, true);
      }
      if (billingSettingsData.acquirer_logo_url) {
        setPreviewAcquirerLogoUrl(billingSettingsData.acquirer_logo_url);
        setFieldValue(
          "acquirer_logo",
          billingSettingsData.acquirer_logo_url,
          true,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingSettingsData, merchantId]);

  const handleLogoFileChange = async (event) => {
    const file = event.target.files?.[0];
    const input = event.target;
    if (!file) {
      return;
    }

    const rejectLogo = (message) => {
      input.value = "";
      setFieldError("logo", message);
      setFieldTouched("logo", true, false);
      dispatch(
        showSnackbar({
          message,
          severity: "error",
        }),
      );
    };

    if (!file.type.startsWith("image/")) {
      rejectLogo("The file must be an image");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      rejectLogo("The image size must be less than 5MB");
      return;
    }

    try {
      const { width, height } = await getImageDimensionsFromFile(file);
      if (!isValidBannerDimensions(width, height)) {
        rejectLogo(BANNER_LOGO_MESSAGES.invalidDimensions);
        return;
      }
    } catch {
      rejectLogo(BANNER_LOGO_MESSAGES.unreadableDimensions);
      return;
    }

    setFieldError("logo", undefined);
    setFieldValue("logo", file, true);
    setFieldValue("logo_action", null, true);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewLogoUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // eslint-disable-next-line no-unused-vars -- referenced from commented Acquirer Logo JSX below
  const handleAcquirerLogoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate image type
      if (!file.type.startsWith("image/")) {
        dispatch(
          showSnackbar({
            message: "The file must be an image",
            severity: "error",
          }),
        );
        return;
      }

      // Validate size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        dispatch(
          showSnackbar({
            message: "The image size must be less than 5MB",
            severity: "error",
          }),
        );
        return;
      }

      setFieldValue("acquirer_logo", file, true);
      setFieldValue("acquirer_logo_action", null, true);

      // Local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewAcquirerLogoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteLogo = () => {
    setPreviewLogoUrl(null);
    setFieldValue("logo", null, true);
    setFieldError("logo", undefined);
    if (modeForm === "update") {
      setFieldValue("logo_action", "nrop", true);
    }
  };

  // eslint-disable-next-line no-unused-vars -- referenced from commented Acquirer Logo JSX below
  const handleDeleteAcquirerLogo = () => {
    setPreviewAcquirerLogoUrl(null);
    setFieldValue("acquirer_logo", null, true);
    if (modeForm === "update") {
      setFieldValue("acquirer_logo_action", "nrop", true);
    }
  };

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    isSubmitting,
  } = useFormik({
    initialValues: billingSettingsInitialValues,
    validationSchema: billingSettingsInitialValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!merchantId) {
        dispatch(
          showSnackbar({
            message: "Merchant ID not found",
            severity: "error",
          }),
        );
        return;
      }

      setSubmitting(true);
      try {
        const payload = {
          token,
          merchant_id: merchantId,
          title: values.title || null,
          contact_email: toContactFormDataValue(values.contact_email),
          contact_phone: toContactFormDataValue(values.contact_phone),
          logo: values.logo instanceof File ? values.logo : null,
          acquirer_logo:
            values.acquirer_logo instanceof File ? values.acquirer_logo : null,
        };

        if (modeForm === "update") {
          if (values.logo_action === "nrop") {
            payload.logo_action = "nrop";
          }
          if (values.acquirer_logo_action === "nrop") {
            payload.acquirer_logo_action = "nrop";
          }
        }

        if (modeForm === "create") {
          await dispatch(createBillingSettings(payload)).unwrap();
          await dispatch(
            getBillingSettingsById({ merchantId, token }),
          ).unwrap();
        } else {
          if (!billingSettingsId) {
            dispatch(
              showSnackbar({
                message: "Configuration ID not found. Cannot update.",
                severity: "error",
              }),
            );
            setSubmitting(false);
            return;
          }
          await dispatch(
            updateBillingSettings({ ...payload, id: billingSettingsId }),
          ).unwrap();
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (statusRetrieve === "loading") {
    return (
      <BillingSettingsFormRootArea>
        <GridContainer>
          <GridItem xs={12}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
              width="100%"
            >
              <CircularProgress />
            </Box>
          </GridItem>
        </GridContainer>
      </BillingSettingsFormRootArea>
    );
  }

  return (
    <BillingSettingsFormRootArea>
      <GridContainer>
        <GridContainer
          justifyContent="space-between"
          alignItems="center"
          padding="15px 0 0"
        >
          <GridItem xs={12}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
              {modeForm === "create"
                ? "Create Billing Settings"
                : "Update Billing Settings"}
            </Typography>
          </GridItem>
        </GridContainer>

        <GridItem xs={12}>
          <form onSubmit={handleSubmit}>
            <GridContainer spacing={2}>
              <GridItem xs={12} padding="15px 15px 15px 0">
                <TextField
                  id="title"
                  name="title"
                  fullWidth
                  label="Title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.title}
                  touched={touched.title}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="contact_email"
                  name="contact_email"
                  fullWidth
                  label="Contact Email"
                  value={values.contact_email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.contact_email}
                  touched={touched.contact_email}
                  inputProps={{ autoComplete: "email" }}
                />
              </GridItem>

              <GridItem xs={12} md={6} padding="15px 15px 15px 0">
                <TextField
                  id="contact_phone"
                  name="contact_phone"
                  fullWidth
                  label="Contact Phone"
                  type="tel"
                  value={values.contact_phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.contact_phone}
                  touched={touched.contact_phone}
                  inputProps={{ autoComplete: "tel" }}
                />
              </GridItem>

              <GridItem xs={12} padding="15px 15px 15px 0">
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Merchant Banner Logo
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {BANNER_LOGO_MESSAGES.aspectRatioHint}
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <label htmlFor="logo-upload">
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      disabled={isLoading}
                      sx={{ display: "none" }}
                    />
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      disabled={isLoading}
                    >
                      Select Logo
                    </Button>
                  </label>
                  {(previewLogoUrl || values.logo) && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteLogo}
                      disabled={isLoading}
                    >
                      Delete Logo
                    </Button>
                  )}
                </Box>
                {previewLogoUrl && (
                  <ImagePreviewContainer>
                    <ImagePreview src={previewLogoUrl} alt="Logo preview" />
                  </ImagePreviewContainer>
                )}
                {touched.logo && errors.logo && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {errors.logo}
                  </Typography>
                )}
              </GridItem>

              {/* Acquirer Logo — hidden in UI by product request; handlers, effect, and payload logic kept for re-enable.
              <GridItem xs={12} padding="15px 15px 15px 0">
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Acquirer Logo
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <label htmlFor="acquirer-logo-upload">
                    <Input
                      id="acquirer-logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleAcquirerLogoFileChange}
                      disabled={isLoading}
                      sx={{ display: "none" }}
                    />
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      disabled={isLoading}
                    >
                      Select Acquirer Logo
                    </Button>
                  </label>
                  {(previewAcquirerLogoUrl || values.acquirer_logo) && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteAcquirerLogo}
                      disabled={isLoading}
                    >
                      Delete Acquirer Logo
                    </Button>
                  )}
                </Box>
                {previewAcquirerLogoUrl && (
                  <ImagePreviewContainer>
                    <ImagePreview
                      src={previewAcquirerLogoUrl}
                      alt="Acquirer logo preview"
                    />
                  </ImagePreviewContainer>
                )}
                {touched.acquirer_logo && errors.acquirer_logo && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {errors.acquirer_logo}
                  </Typography>
                )}
              </GridItem>
              */}

              <GridItem xs={12} padding="0 15px 35px 0">
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 2,
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading || isSubmitting || Boolean(errors.logo)}
                  >
                    {isLoading || isSubmitting
                      ? "Saving..."
                      : modeForm === "create"
                        ? "Create"
                        : "Update"}
                  </Button>
                </Box>
              </GridItem>
            </GridContainer>
          </form>
        </GridItem>
      </GridContainer>
    </BillingSettingsFormRootArea>
  );
};

export default BillingSettingsForm;
